import PropTypes from "prop-types"
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { merge } from "lodash"
import { buildParams } from "../../helpers/data_helper"
import BaseDataTable from "./base-data-table"


/**
 * Tabela com busca, paginação, filtro e ordenação no servidor.
 *
 * Props não listados abaixo são repassados ao BaseDataTable, permitindo customizar a
 * tabela sem alterar este componente. O contrato server-side (state, manual*, on*Change,
 * rowCount) não é sobrescrevível — a paginação depende dele.
 */
const ListDataTable = forwardRef((props, ref) => {
  const { data, columns, showColumnFilters, defaultSorting, columnVisibility, enableRowActions, renderRowActions,
    setData, searchFn, advancedFilters, children, enableRowSelection, getRowId, onRowSelectionChange, enableBatchRowSelection, DetailPanel, enableExpandAll,
    enableColumnsActions, enableTopToolbar, positionActionsColumn, initialState, ...rest } = props

  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRefetching, setIsRefetching] = useState(false)
  const [rowCount, setRowCount] = useState(0)

  const [rowSelection, setRowSelection] = useState({})
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [sorting, setSorting] = useState([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })

  useImperativeHandle(ref, () => ({
    search() {
      fetchData().then()
    },
    getColumnFilters() {
      return columnFilters
    },
    getSorting() {
      return sorting
    }
  }))

  useEffect(() => {
    if (onRowSelectionChange) onRowSelectionChange(rowSelection)
  }, [rowSelection])

  useEffect(() => {
    fetchData().then()
  }, [
    columnFilters,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    advancedFilters
  ])

  const fetchData = async () => {
    if (!(data || []).length) {
      setIsLoading(true)
    } else {
      setIsRefetching(true)
    }

    try {
      const sort = []
      if (Array.isArray(sorting) && sorting.length > 0) {
        sort.push(...sorting)
      } else if (Array.isArray(defaultSorting) && defaultSorting.length > 0) {
        sort.push(...defaultSorting)
      }
      const result = await searchFn(buildParams(pagination, globalFilter, columnFilters, advancedFilters, columns, sort))
      setData(result.data || [])
      setRowCount(result.count)
    } catch (error) {
      setIsError(true)
      console.error(error)
      return
    }

    setIsError(false)
    setIsLoading(false)
    setIsRefetching(false)
  }

  return (
    <React.Fragment>
      <BaseDataTable
        {...rest}
        columns={columns}
        data={data}
        initialState={merge({}, initialState, { showColumnFilters, columnVisibility: columnVisibility || {} })}
        manualFiltering
        manualPagination
        manualSorting
        enableRowActions={enableRowActions}
        positionActionsColumn={positionActionsColumn}
        renderRowActions={({ row, table }) => renderRowActions(row, table)}
        muiToolbarAlertBannerProps={
          isError
            ? {
              color: "error",
              children: "Não foi possível buscar os dados!"
            }
            : undefined
        }
        onColumnFiltersChange={setColumnFilters}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        rowCount={rowCount}
        state={{
          columnFilters,
          globalFilter,
          isLoading,
          pagination,
          showAlertBanner: isError,
          showProgressBars: isRefetching,
          sorting,
          rowSelection
        }}
        // enableRowSelection={enableRowSelection}
        enableRowSelection={enableRowSelection && ((row) => {
          if (typeof enableRowSelection == "function") {
            return enableRowSelection(row)
          } else {
            return enableRowSelection
          }
        })}
        enableBatchRowSelection={enableBatchRowSelection}
        getRowId={getRowId}
        onRowSelectionChange={(row) => {
          setRowSelection(row)
        }} //connect internal row selection state to your own
        enableExpandAll={props.enableExpandAll}
        renderDetailPanel={DetailPanel ? ((row) => {
          return (<>
              <DetailPanel row={row}>
              </DetailPanel>
            </>
          )
        }):null
        }
        enableColumnActions={enableColumnsActions != null ? enableColumnsActions : true}
        enableTopToolbar={enableTopToolbar != null ? enableTopToolbar : true}
      />

      {children}
    </React.Fragment>
  )
})

ListDataTable.propTypes = {
  data: PropTypes.any,
  columns: PropTypes.any,
  showColumnFilters: PropTypes.bool,
  advancedFilters: PropTypes.any,
  pagination: PropTypes.any,
  defaultSorting: PropTypes.any,
  globalFilter: PropTypes.string,
  rowCount: PropTypes.number,
  onColumnFiltersChange: PropTypes.func,
  onGlobalFilterChange: PropTypes.func,
  onPaginationChange: PropTypes.func,
  onSortingChange: PropTypes.func,
  setRowCount: PropTypes.func,
  setData: PropTypes.func,
  searchFn: PropTypes.func,
  enableRowSelection: PropTypes.any,
  getRowId: PropTypes.func,
  onRowSelectionChange: PropTypes.func,
  DetailPanel: PropTypes.any,
  enableExpandAll: PropTypes.bool,
  enableColumnsActions: PropTypes.bool,
  positionActionsColumn: PropTypes.string,
  initialState: PropTypes.object,
}

export default ListDataTable
